// types
import type {
  ActivityResponse,
  ClientResponse,
  EmployeeResponse,
  EventLocationResponse,
  EventResponse,
  EventWarningsResponse,
  GetPlanningRequest,
  PlanAppointmentsRequest,
  PlanningEntityIdentifier,
  PlanningEntityMetricsResponse,
  PlanningEntityResponse,
  PlanningEntityType,
  PlanningResponse,
  RemoveAssignmentFromAppointmentsRequest,
  ScheduleResponse,
  ViewportContext
} from 'generated';

import type { Event, PlanningEntityMap } from './types';
import type {
  Dropzone,
  PlanningTask
} from 'components/shared/TaskList/taskListData.types';
import type { Period } from 'components/shared/DateNavigation/DateNavigation.types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';

// constants
import { BLUE_DARK, GRAY_SECONDARY, PRIMARY } from 'constants/colors';

// utils
import { format } from 'date-fns';
import { filterAndAddDropzones } from './CardsView/utils/utils';
import dayjs, { type Dayjs } from 'dayjs';
import { View, ViewContext } from './enums';
import { requests } from 'RestAPIs/rapini';
import i18n from 'config/i18n';
import { getLocale } from 'utils/date';
import type { DialogManagerActive, ShouldShowPopoverDialog } from './atoms';
import { isPopoverDialogCleared } from './atoms';
import { apDebug } from './apDebug';
import {
  armDropVerification,
  expectDropRefetch,
  verifyDropAfterRefresh
} from './dropVerification';
import { DialogType } from './components/DialogManager/types';
import { isEqual, omit } from 'lodash-es';

export const CORRELATION_TOKEN_SESSION_KEY = 'CORRELATION_TOKEN';

export type APNotificationKey = `activityPlanning.${string}`;

export type APNotification = {
  notificationLevel: 'WARNING';
  referenceId: string;
  text: APNotificationKey;
} & { messages: { meta: { name: string; time: string } } }[];

export type APWarningNotification = {
  [key: APNotificationKey]: {
    translationKey: string;
    messages: {
      meta: {
        name: string;
        time: string;
      };
    }[];
  };
};

export function findContainer(
  planningEntities: PlanningEntityMap | null,
  id: string
) {
  if (!planningEntities) return null;
  if (id in planningEntities) {
    return id;
  }

  return (
    Object.keys(planningEntities).find((key) => {
      return (
        planningEntities[key].schedules
          .flatMap((s) => s.events)
          .find(({ id: eventId }) => eventId === id) ||
        planningEntities[key].events.find(({ id: eventId }) => eventId === id)
      );
    }) ?? null
  );
}

export function findItem(
  planningEntities: PlanningEntityMap | null,
  planningEntityId: string,
  id: string
) {
  if (!planningEntities || planningEntityId === undefined) return null;

  return (
    planningEntities[planningEntityId].schedules
      .map((s) => s.events)
      .flat()
      .find(({ id: eventId }) => eventId === id) ??
    planningEntities[planningEntityId].events.find(
      ({ id: eventId }) => eventId === id
    )
  );
}

export function findEmployee(
  events: (EventResponse | Event)[],
  appointmentId: string | number,
  employeeId: string | number
) {
  if (events?.length === 0 || !appointmentId || !employeeId) return null;
  return events.find(
    (event) =>
      event.id === appointmentId &&
      event.employees?.map(({ id }) => id).includes(employeeId as number)
  )?.employees?.[0];
}

export const getAddressNotFound = (
  location: EventLocationResponse | undefined,
  activity: ActivityResponse
) => {
  const addressNotFound: boolean =
    location?.locationType === 'CLIENTADDRESS' &&
    (!location?.latitude || !location?.longitude) &&
    activity?.clientRelatedType !== 'NON_CLIENT_RELATED';
  return addressNotFound;
};

export const getHasWarnings = (
  warnings: Pick<
    EventWarningsResponse,
    | 'startsTooEarly'
    | 'startsTooLate'
    | 'excludedEmployeeClientCombination'
    | 'hasOverlap'
  > & { isUrgent: boolean; isInSyncWithEcd: boolean }
) => {
  const {
    startsTooEarly,
    startsTooLate,
    isInSyncWithEcd,
    excludedEmployeeClientCombination,
    isUrgent,
    hasOverlap
  } = warnings;
  return (
    (isUrgent && (startsTooEarly || startsTooLate)) ||
    !isInSyncWithEcd ||
    excludedEmployeeClientCombination ||
    hasOverlap
  );
};

export const getWarnings = (
  startsTooEarly: boolean,
  startsTooLate: boolean,
  hasOverlap: boolean,
  addressNotFound: boolean,
  overQualified: boolean,
  underQualified: boolean,
  excludedEmployeeClientCombination: boolean,
  excludedEmployeeClientCombinationDescription: string,
  isInSyncWithEcd: boolean
) => {
  const warnings: string[] = [];
  if (startsTooEarly || startsTooLate) {
    warnings.push(
      `Afspraak staat ${startsTooEarly ? 'te vroeg' : 'te laat'} ingepland`
    );
  }
  if (excludedEmployeeClientCombination) {
    warnings.push(excludedEmployeeClientCombinationDescription);
  }
  if (overQualified) {
    warnings.push('Overkwalificatie');
  }
  if (underQualified) {
    warnings.push('Onderkwalificatie');
  }
  if (!isInSyncWithEcd) {
    warnings.push(
      'Medewerker bij deze afspraak en bij de gekoppelde dienst zijn niet in sync'
    );
  }
  if (addressNotFound) {
    warnings.push('Adres niet gevonden');
  }
  if (hasOverlap) {
    warnings.push('Afspraak heeft overlap');
  }
  return warnings;
};

export const getClientName = (client: ClientResponse | undefined | null) => {
  if (!client) return '';
  return client.displayName || client.nameWithInitials || '';
};

export const getClientInitials = (
  client: ClientResponse | undefined | null
) => {
  if (!client) return '';
  const clientName = client
    ? `${client.firstName?.substring(0, 1)}${client.lastName?.substring(0, 1)}`
    : '';

  return clientName;
};

export const getClientPhoneNumbers = (
  client: ClientResponse | undefined | null
) => {
  if (!client) return null;
  const phoneNumbers =
    client && client.phoneNumbers && client.phoneNumbers.length > 0
      ? client.phoneNumbers
      : null;
  return phoneNumbers;
};

export const getClientKeyArrangements = (
  client: ClientResponse | undefined | null
) => {
  if (!client) return null;
  const keyArrangements =
    client && client.keyArrangements && client.keyArrangements.length > 0
      ? client.keyArrangements
      : null;
  return keyArrangements;
};

export const getClientPlanningFeatures = (
  client: ClientResponse | undefined | null
) => {
  if (!client) return null;
  const planningFeatures =
    client && client.planningFeatures && client.planningFeatures.length > 0
      ? client.planningFeatures
      : null;
  return planningFeatures;
};

export const getEmployeeName = (
  employee: EmployeeResponse | undefined
): string => {
  const employeeName = employee
    ? employee.nameInformal || employee.nameCompound || ''
    : '';

  return employeeName;
};

export const getEmployeeInitials = (employee?: EmployeeResponse) => {
  return employee
    ? `${(employee.firstName || employee.nameInformal)?.substring(0, 1)}${(
        employee.lastName || employee.nameInformal
      )?.substring(0, 1)}`
    : '';
};

// TODO: add refId to events (frontend only)
function prepareForFE(
  planning: PlanningResponse,
  thresholdInMinutes: number
): PlanningResponse {
  const planningWithDropZones: PlanningResponse = {
    ...planning,
    planningEntities: planning?.planningEntities?.map((planningEntity) => ({
      ...planningEntity,
      schedules: planningEntity?.schedules?.map((schedule) => ({
        ...schedule,
        events: filterAndAddDropzones(
          schedule.events.map((e) => ({
            ...e,
            scheduleStart: schedule.start
          })),
          thresholdInMinutes
        ) as EventResponse[]
      }))
    }))
  };

  return planningWithDropZones;
}

export const toPlanningTasksWithDropzones = (
  planningTasks: PlanningTask[] = []
) =>
  planningTasks.map((task) => {
    if (typeof task !== 'object' || !task) {
      return task;
    }

    const { data: event, ...rest } = task;

    if (event?.durationInSeconds) {
      const durationInHours = event.durationInSeconds / 3600;
      const filteredData = filterAndAddDropzones([event], durationInHours);

      // Map items to the correct type
      const mappedData = filteredData.map((item) =>
        'date' in item && 'end' in item
          ? (item as EventResponse)
          : (item as Dropzone)
      );

      return { ...rest, data: mappedData };
    }

    return task;
  });

export function normalizePlanningData(
  planning: PlanningResponse,
  thresholdInMinutes: number
): PlanningEntityMap {
  const planningWithDropzones = prepareForFE(planning, thresholdInMinutes);

  const planningEntities: PlanningEntityMap =
    planningWithDropzones?.planningEntities?.reduce((acc, cur) => {
      const normalizedCur = cur?.planningEntityIdentifier
        ? {
            ...cur,
            planningEntityIdentifier: normalizePlanningEntityIdentifier(
              cur.planningEntityIdentifier
            )
          }
        : cur;
      const id =
        normalizedCur.planningEntityIdentifier?.entityId ??
        normalizedCur.planningEntityIdentifier?.externalEntityId ??
        normalizedCur.id ??
        '';
      if (acc[id] != null && normalizedCur != null) {
        acc[id] = combinePlanningEntities(normalizedCur, acc[id]);
      }
      acc[id] = normalizedCur || {};

      return acc;
    }, {});

  return planningEntities;
}

export function combinePlanningEntities(
  planningEntity1: PlanningEntityResponse,
  planningEntity2: PlanningEntityResponse
): PlanningEntityResponse | null {
  if (planningEntity1 == null || planningEntity2 == null) {
    return null;
  }

  planningEntity1.events = planningEntity1.events.concat(
    planningEntity2.events || []
  );
  planningEntity1.schedules = planningEntity1.schedules.concat(
    planningEntity2.schedules || []
  );

  if (planningEntity2.absences?.length ?? 0 > 0) {
    planningEntity1.absences = planningEntity1.absences
      ? planningEntity1.absences.concat(planningEntity2.absences || [])
      : planningEntity2.absences;
  }

  planningEntity1.metrics = combinePlanningEntityMetricsResponse(
    planningEntity1.metrics,
    planningEntity2.metrics
  );

  return planningEntity1;
}

export function combinePlanningEntityMetricsResponse(
  metrics1: PlanningEntityMetricsResponse | undefined,
  metrics2: PlanningEntityMetricsResponse | undefined
): PlanningEntityMetricsResponse | undefined {
  if (!metrics1 || !metrics2) return metrics1 || metrics2;

  return {
    contractDurationInSeconds:
      (metrics1.contractDurationInSeconds ?? 0) +
      (metrics2.contractDurationInSeconds ?? 0),
    plannedDurationInSeconds:
      (metrics1.plannedDurationInSeconds ?? 0) +
      (metrics2.plannedDurationInSeconds ?? 0)
  };
}

export const getWarningMetadata = (
  activeItem: Event | null
): { name: string; time: string } => {
  const meta: { name: string; time: string } = {
    name: '-',
    time: '-'
  };

  if (!activeItem) return meta;

  const { clients, start, activity } = activeItem;
  return {
    name: clients[0] ? getClientName(clients[0]) : activity?.name || '-',
    time: format(new Date(start), 'HH:mm')
  };
};

export const getConflictingEvents = (
  events: Event[],
  notifications: APNotification[]
) => {
  return events.filter(({ id }) =>
    notifications.map(({ referenceId }) => referenceId).includes(id)
  );
};

export const planningEntitiesToEvents = (
  planningEntities: PlanningEntityMap | null
) => {
  if (!planningEntities) return [];
  const schedules = Object.values(planningEntities)
    ?.filter((entity) => entity.schedules)
    .map((entity) => entity.schedules as ScheduleResponse[])
    .flat();
  const events = schedules.map((schedule) => schedule.events as Event[]).flat();
  return events;
};

export const notificationsToHashTable = (
  conflictingEvents: Event[],
  notifications: APNotification[]
) => {
  const table: {
    [key: APNotificationKey]: {
      translationKey: string;
      messages: {
        meta: {
          name: string;
          time: string;
        };
      }[];
    };
  } = {
    'activityPlanning.appointment.outside.timeWindow': {
      translationKey: 'activityPlanning.nonrecommendedTimeslot',
      messages: []
    }
    // Other future notifications ...
  };

  notifications.forEach((n: APNotification) => {
    const event =
      conflictingEvents.filter(({ id }) => id === n.referenceId)[0] || null;
    if (event) table[n.text].messages.push({ meta: getWarningMetadata(event) });
  });

  return table;
};

export const getActivityTypeColor = (isClient: boolean, isAbsent: boolean) => {
  return isClient ? PRIMARY : isAbsent ? GRAY_SECONDARY : BLUE_DARK;
};

const getAllClientsOrEmployees = (
  key: 'clients' | 'employees',
  planningEntities: PlanningEntityMap
) => {
  return Object.values(planningEntities).flatMap((e) => {
    return [
      ...(e.schedules || []).flatMap((s) => {
        return s.events
          .filter((ev) => ev[key])
          .flatMap((ev: any) => {
            return ev[key];
          });
      }),
      ...(e.events || [])
        .filter((ev) => ev[key])
        .flatMap((ev: any) => {
          return ev[key];
        })
    ];
  });
};

const getSortedUniqueClientsOrEmployees = (
  a: { id: string | number; lastName: string }[]
) =>
  a
    .filter((b, index) => {
      return a.findIndex((c) => c.id === b.id) === index;
    })
    .sort((a, b) => {
      return a.lastName && b.lastName
        ? a.lastName.localeCompare(b.lastName)
        : 0;
    });

export const getClientsOrEmployeesList = (
  planningEntities: PlanningEntityMap
) => {
  if (!planningEntities)
    return {
      clients: [],
      employees: []
    };

  const allClients = getAllClientsOrEmployees('clients', planningEntities);
  const allEmployees = getAllClientsOrEmployees('employees', planningEntities);

  const clients = getSortedUniqueClientsOrEmployees(
    allClients
  ) as ClientResponse[];

  const employees = getSortedUniqueClientsOrEmployees(
    allEmployees
  ) as EmployeeResponse[];

  return { clients, employees };
};

export const getPlanningEntityTypeByView = (
  planningEntityType: PlanningEntityType,
  view: View
) => {
  if (
    view === View.Cards ||
    view === View.CardsPragmatic ||
    view === View.Map
  ) {
    return 'ROUTE';
  }

  return planningEntityType;
};

// Legacy dnd-kit ↔ Pragmatic view pairs: a saved view whose toggle is off
// falls back to its counterpart when only one half of the pair is enabled.
const VIEW_MIGRATIONS: Partial<Record<View, View>> = {
  [View.Roster]: View.RosterPragmatic,
  [View.RosterPragmatic]: View.Roster,
  [View.Cards]: View.CardsPragmatic,
  [View.CardsPragmatic]: View.Cards,
  [View.Timeline]: View.TimelinePragmatic,
  [View.TimelinePragmatic]: View.Timeline
};

export type ActivityPlanningViewGrants = {
  hasActivityPlanningViewAccess?: boolean;
  hasWorkPlacePlanningViewAccess?: boolean;
  hasMyActivitiesProjectAccess?: boolean;
  hasManageOwnProjectsAccess?: boolean;
  hasKidsPlanAccess?: boolean;
};

// Single source of truth for which views a user can reach, shared by the
// view switcher (what to list) and the initial view resolution (what to
// fetch for on first render) -- calling this from only one of the two let
// the other keep rendering/fetching for a view neither offers nor migrates
// away from until a post-mount effect caught up a render late.
export const getAvailableViews = (
  grants: ActivityPlanningViewGrants,
  viewContext: ViewContext
): View[] => {
  const {
    hasActivityPlanningViewAccess,
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasKidsPlanAccess
  } = grants;

  let result: View[] = [];
  if (
    hasActivityPlanningViewAccess ||
    hasWorkPlacePlanningViewAccess ||
    hasMyActivitiesProjectAccess ||
    hasManageOwnProjectsAccess ||
    hasKidsPlanAccess
  ) {
    result.push(View.RosterPragmatic);
  }
  if (
    hasActivityPlanningViewAccess ||
    hasWorkPlacePlanningViewAccess ||
    hasMyActivitiesProjectAccess ||
    hasManageOwnProjectsAccess
  ) {
    result.push(View.List);
  }
  if (hasActivityPlanningViewAccess) {
    result.push(View.TimelinePragmatic);
    result.push(View.CardsPragmatic);
    result.push(View.Map);
  }

  // Only Roster and List view when viewing My Activities
  if (viewContext === ViewContext.Own) {
    result = result.filter(
      (v) => v === View.List || v === View.RosterPragmatic
    );
  }

  return result;
};

export const resolveAvailableView = (
  view: View | undefined,
  availableViews: View[]
): View | null => {
  if (availableViews.length === 0) return null;
  if (view && availableViews.includes(view)) return view;

  const migrated = view ? VIEW_MIGRATIONS[view] : undefined;
  if (migrated && availableViews.includes(migrated)) return migrated;

  return availableViews[0];
};

export const getEntityPreferenceKey = (view: View): View => {
  if (view === View.RosterPragmatic) return View.Roster;
  if (view === View.CardsPragmatic) return View.Cards;
  if (view === View.TimelinePragmatic) return View.Timeline;
  return view;
};

export const getViewContext = (): ViewContext => {
  const context = window.location.hash.split(',')?.[1]?.split('&')?.[0];
  return context === ViewContext.Own ? ViewContext.Own : ViewContext.Team;
};

export const getSelectableStatus = (event: EventResponse) =>
  getViewContext() === ViewContext.Own
    ? event.isSubmittable
    : event.isApprovable;

export const getEventsByWeekDay = (
  arr,
  period: Period | null
): Record<number, any[]> => {
  return arr.reduce((acc, cur) => {
    const dayOfWeek = dayjs(cur.date).isoWeekday();
    if (period === 'workweek' && dayOfWeek >= 6) return acc;

    acc[dayOfWeek] = acc[dayOfWeek] || [];
    acc[dayOfWeek].push(cur);
    return acc;
  }, {});
};

export function getAbsencesOrAvailabilitiesByWeekDay<
  T extends { start: string; end: string; type?: string }
>(absencesOrAvailabilities: T[], period: Period | null) {
  return absencesOrAvailabilities.reduce((acc, cur: T) => {
    const startDate = dayjs(cur.start);
    const endDate = dayjs(cur.end);

    const absenceIsSingleDay = startDate.isSame(endDate, 'day');
    const isWorkweek = period === 'workweek';
    const startDateIsWithinWorkweek = startDate.isoWeekday() < 6;

    if (!absenceIsSingleDay) {
      const dayOfWeekStartAbsence = startDate.isoWeekday();
      const dayOfWeekEndAbsence = endDate.isoWeekday();
      const daysAbsent =
        Math.abs(dayjs(cur.start).diff(endDate.toISOString(), 'days')) + 1;
      Array.from(Array(daysAbsent).keys()).forEach((_, i: number) => {
        const day = dayOfWeekStartAbsence + i;
        const dayObj: T = { ...cur };
        const currentDate = dayjs(dayObj.start).add(i, 'day');
        const currentDateIsWithinWorkweek = currentDate.isoWeekday() < 6;

        // Set end time of first day to 23:59
        if (day === dayOfWeekStartAbsence) {
          dayObj.end = dayjs(dayObj.end)
            .set('hour', 23)
            .set('minute', 59)
            .toISOString();
        }

        // Set start day of every other in between day to 00:00
        if (day !== dayOfWeekStartAbsence && day !== dayOfWeekEndAbsence) {
          dayObj.start = dayjs(dayObj.start)
            .set('hour', 0)
            .set('minute', 0)
            .toISOString();
        }

        if (!isWorkweek || (isWorkweek && currentDateIsWithinWorkweek))
          acc[currentDate.format('DD-MM-YYYY')] = dayObj;
      });
    }

    // If single day
    if (
      absenceIsSingleDay &&
      (!isWorkweek || (isWorkweek && startDateIsWithinWorkweek))
    )
      acc[startDate.format('DD-MM-YYYY')] = cur;

    return acc;
  }, {});
}

export const isWithinDay = (date: string | Dayjs | Date): boolean => {
  return dayjs(date).isAfter(0, 'hour') && dayjs(date).isBefore(23, 'hour');
};

export const appendCorrelationToken = <T extends object>(payload: T) => {
  const correlationToken: string =
    sessionStorage?.getItem(CORRELATION_TOKEN_SESSION_KEY) ||
    window.btoa(JSON.stringify({ ts: Date.now() }));

  sessionStorage?.setItem(CORRELATION_TOKEN_SESSION_KEY, correlationToken);

  return {
    ...payload,
    correlationToken
  } as T & { correlationToken: string };
};

export const normalizePlanningEntityIdentifier = (
  identifier: PlanningEntityIdentifier
): PlanningEntityIdentifier => ({
  ...identifier,
  id: identifier.id != null ? Number(identifier.id) : undefined,
  externalEntityId:
    identifier.externalEntityId != null
      ? identifier.externalEntityId
      : undefined,
  externalId:
    identifier.externalId != null ? Number(identifier.externalId) : undefined,
  number: identifier.number != null ? Number(identifier.number) : undefined,
  contractNumber:
    identifier.contractNumber != null
      ? Number(identifier.contractNumber)
      : undefined
});

const isSamePlanningEntityIdentifier = (
  a?: PlanningEntityIdentifier,
  b?: PlanningEntityIdentifier
): boolean => {
  if (!a || !b) return false;

  return isEqual(omit(a, ['start']), omit(b, ['start']));
};

export const getCorrelationToken = (): string | null =>
  sessionStorage?.getItem(CORRELATION_TOKEN_SESSION_KEY);

let _skipNextActivityPlanningUpdatedEvent = false;

export const markSkipNextActivityPlanningUpdatedEvent = () => {
  _skipNextActivityPlanningUpdatedEvent = true;
};

export const isEqualToSessionCorrelationToken = (
  correlationToken: string | null | undefined
): boolean => {
  const sessionCorrelationToken =
    sessionStorage?.getItem(CORRELATION_TOKEN_SESSION_KEY) || '';
  return correlationToken && sessionCorrelationToken
    ? correlationToken === sessionCorrelationToken
    : false;
};

/// TODO: currently not implemented
export const isValidCorrelationToken = (): boolean => {
  const VALIDITY_IN_MS = 5000;
  const token = window.atob(
    sessionStorage?.getItem(CORRELATION_TOKEN_SESSION_KEY) || ''
  );

  if (!token) return false;

  const decodedToken = JSON.parse(token) as { ts: number };
  return decodedToken.ts + VALIDITY_IN_MS > Date.now();
};

export const isJSONString = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
};

export const scrollToAppointment = (
  appointmentId: string,
  date: string | Date,
  delayInMS = 0
) => {
  setTimeout(() => {
    const element = document.querySelector(
      `.__${appointmentId}__.__${dayjs(date).format('YYYY-MM-DD')}__`
    );
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest'
    });
  }, delayInMS);
};

export const scrollToRoute = (
  appointmentGroupingEntityId: string,
  delayInMS = 0
) => {
  setTimeout(() => {
    const intervalId = setInterval(() => {
      const element = document.querySelector(
        `.__${appointmentGroupingEntityId}__`
      );

      if (element) {
        clearInterval(intervalId);
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
      }
    }, 100);
  }, delayInMS);
};

/**
 * The row the dragged occurrences are coming from, as a partial viewport context to spread into a plan payload.
 *
 * The identifier is taken straight out of `planningEntities`, which is also what `currentPlanningEntityIdentifiers`
 * is built from — so what we send here is literally an element of the viewport list we send alongside it, and the
 * backend can match the two whole. That lets it scope its schedule read to target + source in one call instead of
 * reading the occurrences first to discover where they live.
 *
 * Returns undefined when the row cannot be resolved. The field is optional and advisory: the backend verifies the
 * occurrences really are on the row we name, and derives the source itself when they are not.
 */
export const sourceViewportContext = (
  planningEntities: PlanningEntityMap | null | undefined,
  activePlanningEntityId: string | undefined
): Pick<ViewportContext, 'sourcePlanningEntityIdentifiers'> | undefined => {
  const identifier =
    activePlanningEntityId != null
      ? planningEntities?.[activePlanningEntityId]?.planningEntityIdentifier
      : undefined;

  return identifier
    ? { sourcePlanningEntityIdentifiers: [identifier] }
    : undefined;
};

/** The callbacks a view's mutation reports its result through. */
export interface PlanAppointmentsMutationCallbacks {
  onSuccess: (response: any) => void;
  onError: (error?: unknown) => void;
}

export interface HandlePlanAppointmentsOptions {
  payload: PlanAppointmentsRequest | null;
  planAppointments: (
    payload: PlanAppointmentsRequest,
    callbacks: PlanAppointmentsMutationCallbacks
  ) => void;
  setPayload: (payload: PlanAppointmentsRequest) => void;
  setShouldShowPopoverDialog: (value: any) => void;
  setDialogState: (dialogState: DialogManagerActive) => void;
  onMutationError: (error?: unknown) => void;
  planningEntitiesToEvents: (planningEntities: any) => Event[];
  getConflictingEvents: (
    events: Event[],
    notifications: APNotification[]
  ) => Event[];
  notificationsToHashTable: (
    events: Event[],
    notifications: APNotification[]
  ) => any;
  planningEntities?: any;
  appendCorrelationToken?: (
    payload: PlanAppointmentsRequest
  ) => PlanAppointmentsRequest;
  setPlanResponsePlanningEntityIdentifiers: ({
    ts,
    planningEntityIdentifiers
  }: {
    ts: number;
    planningEntityIdentifiers: PlanningEntityIdentifier[];
  }) => void;
  // Accepts an updater as well as a value: the inline-response path below has to
  // splice into the atom as it is when the response lands. Every caller passes
  // the Recoil setter straight through, so both forms are supported.
  setPlanningEntities?: (
    value:
      | PlanningEntityMap
      | null
      | ((current: PlanningEntityMap | null) => PlanningEntityMap | null)
  ) => void;
  viewport?: ViewportContext;
  onCancelWarningDialog?: () => void;
  onSuccess?: () => void;
  onComplete?: () => void;
}

export const handlePlanAppointments = (
  options: HandlePlanAppointmentsOptions
) => {
  const {
    payload,
    planAppointments,
    setPayload,
    setShouldShowPopoverDialog,
    setDialogState,
    onMutationError,
    planningEntitiesToEvents,
    getConflictingEvents,
    notificationsToHashTable,
    planningEntities,
    appendCorrelationToken,
    setPlanResponsePlanningEntityIdentifiers,
    setPlanningEntities,
    viewport,
    onCancelWarningDialog,
    onSuccess,
    onComplete
  } = options;

  if (payload) {
    setPayload(payload);

    // Apply appendCorrelationToken if provided (for RosterView)
    let planPayload = appendCorrelationToken?.(payload) ?? payload;

    // Enrich with viewport object if all viewport properties are present and non-empty.
    // Spread the payload's own viewport first: the drop handler put the source row there, and it has to survive
    // this merge — including across the recurring-appointment popover, which replays the stored payload later.
    if (
      viewport &&
      viewport.startDate &&
      viewport.endDate &&
      viewport.planningEntityIdentifiers &&
      viewport.planningEntityIdentifiers.length > 0
    ) {
      planPayload = {
        ...planPayload,
        viewport: {
          ...planPayload.viewport,
          startDate: viewport.startDate,
          endDate: viewport.endDate,
          planningEntityIdentifiers: viewport.planningEntityIdentifiers
        }
      };
    } else if (planPayload.viewport) {
      // No viewport to send. The source row is only meaningful as a subset of one, and a viewport carrying nothing
      // but a source row would read as "the board is these zero rows" on the backend — so drop it with the rest.
      planPayload = { ...planPayload, viewport: undefined };
    }

    apDebug('planRequest', {
      occurrenceIds: planPayload.appointmentOccurrences?.map(
        ({ entityId }) => entityId
      ),
      planningEntity: planPayload.planningEntity,
      start: planPayload.start,
      editAppointmentType: planPayload.editAppointmentType,
      suppressWarnings: planPayload.suppressWarnings,
      hasViewport: !!planPayload.viewport
    });

    planAppointments(planPayload, {
      onSuccess: (response: any) => {
        apDebug('planResponse', {
          success: response.success,
          path:
            response.planningEntities &&
            response.planningEntities.length > 0 &&
            setPlanningEntities &&
            planningEntities
              ? 'inline'
              : 'refetch',
          inlineEntityIds: response.planningEntities?.map(
            (entity: any) =>
              entity.planningEntityIdentifier?.entityId ??
              entity.planningEntityIdentifier?.externalEntityId ??
              entity.id
          ),
          responseIdentifiers: response.planningEntityIdentifiers,
          notificationCount: response.notifications?.length ?? 0
        });
        if (!response.success && planningEntities) {
          const events: Event[] = planningEntitiesToEvents(planningEntities);
          const notifications = response.notifications as APNotification[];
          const conflictingEvents: Event[] = getConflictingEvents(
            events || [],
            notifications
          );

          setDialogState({
            type: DialogType.WARNING,
            open: true,
            data: notificationsToHashTable(conflictingEvents, notifications),
            onSubmit: () => {
              // Retry with suppressWarnings: true
              handlePlanAppointments({
                ...options,
                payload: {
                  ...planPayload,
                  suppressWarnings: true
                } as PlanAppointmentsRequest
              });
            },
            onCancel: () => {
              onCancelWarningDialog?.();
            }
          });
        }
        if (response.success) {
          // The backend confirmed the move — from here on, whatever refreshed
          // data lands (inline below, or the refetch merge in `usePlanning`)
          // must reflect it. Scoped to the events this call carries, so a plan
          // call from another view cannot arm a leftover Cards-view drop. See
          // `dropVerification.ts`.
          armDropVerification(
            planPayload.appointmentOccurrences
              ?.map(({ entityId }) => entityId)
              .filter((id): id is string => id != null)
          );

          // Check if inline entities are available in the response
          const hasInlineEntities =
            response.planningEntities && response.planningEntities.length > 0;

          if (hasInlineEntities && setPlanningEntities && planningEntities) {
            // Step 1: Normalize inline planning entities
            const normalizedInlineEntities = normalizePlanningData(
              {
                planningEntities: response.planningEntities
              } as PlanningResponse,
              0 // No threshold needed for inline entities
            );

            // Step 2 + 3: Splice the inline entities into the atom as it is
            // *now*, through an updater. `planningEntities` was captured when
            // this call started, so writing a map built from it would undo
            // anything that landed since — with two drops in flight the later
            // response drops the earlier one's column off the board, and step 4
            // suppresses the refetch that would have repaired it. A cleared
            // board stays cleared: the view moved on (different plan unit or
            // date range) and this response no longer describes what is on
            // screen.
            setPlanningEntities((current: PlanningEntityMap | null) =>
              current === null
                ? current
                : { ...current, ...normalizedInlineEntities }
            );

            // Outside the updater on purpose: React may invoke updaters more
            // than once, and the verification consumes itself on first run.
            verifyDropAfterRefresh(normalizedInlineEntities, 'inline');

            // Step 4: Assume inline entities are authoritative; skip refetch
            markSkipNextActivityPlanningUpdatedEvent();
          } else {
            // Step 5: Fallback — inline entities not available or not provided
            // Use existing behavior: trigger GET refetch for all affected entities
            const refetchTs = Date.now();
            // The drop's own refetch lands under this timestamp; scope the
            // verification to it so an unrelated fetch cannot consume the check.
            expectDropRefetch(refetchTs);
            setPlanResponsePlanningEntityIdentifiers({
              ts: refetchTs,
              planningEntityIdentifiers: (
                response.planningEntityIdentifiers || []
              ).map(normalizePlanningEntityIdentifier)
            });
          }

          onSuccess?.();
        }
        onComplete?.();
      },
      onError: onMutationError
    });
  }
  // Value-stable reset. Writing a fresh `{ id: '' }` when nothing is open still
  // notifies every subscriber of the atom, and in the pragmatic cards view every
  // card on the board subscribes — so an ordinary drop re-rendered the whole
  // board from here, inside the drop event, competing with the paint that shows
  // the drop feedback. Returning the previous value makes Recoil skip it.
  //
  // Skipped only when the value is *fully* cleared, not just when `id` is empty:
  // this is a full replace, so keeping a value that still carries flags from an
  // earlier action would leave them live for every view reading this atom.
  setShouldShowPopoverDialog((previous: ShouldShowPopoverDialog) =>
    isPopoverDialogCleared(previous) ? previous : { id: '' }
  );
};

export const unassignAppointment = async (options: {
  editAppointmentType: EditAppointmentType;
  activeEvent: Event | null;
  selectedIds: string[];
  planningEntities: any;
  appendCorrelationToken?: (
    payload: RemoveAssignmentFromAppointmentsRequest
  ) => RemoveAssignmentFromAppointmentsRequest;
  shouldShowPopoverDialog: any;
  unassignAppointmentAsync: (
    payload: RemoveAssignmentFromAppointmentsRequest
  ) => Promise<any>;
  setShouldShowPopoverDialog: (value: any) => void;
  setPlanResponsePlanningEntityIdentifiers: ({
    ts,
    planningEntityIdentifiers
  }: {
    ts: number;
    planningEntityIdentifiers: PlanningEntityIdentifier[];
  }) => void;
  setActiveEntityContainingSelection: (entityId: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  onFinally?: () => void;
  onGetEvents: (
    planningEntity: any,
    isToPlan: boolean,
    selectedIds: string[]
  ) => Event[];
}) => {
  const {
    editAppointmentType,
    activeEvent,
    selectedIds,
    planningEntities,
    appendCorrelationToken,
    shouldShowPopoverDialog,
    unassignAppointmentAsync,
    setShouldShowPopoverDialog,
    setPlanResponsePlanningEntityIdentifiers,
    setIsLoading,
    onGetEvents,
    onFinally
  } = options;

  const unassignAppointmentId: string =
    shouldShowPopoverDialog.id || activeEvent?.id || '';
  const container = findContainer(planningEntities, unassignAppointmentId);
  const planningEntity = planningEntities?.[container as string];
  const isToPlan = planningEntity?.type === 'ORGANIZATIONAL_UNIT_TO_PLAN';

  const event: Event | null =
    activeEvent ||
    planningEntity?.schedules
      ?.flatMap((s: any) => s.events)
      .find(({ id }: { id: string }) => id === unassignAppointmentId) ||
    null;

  const events: Event[] = onGetEvents(planningEntity, isToPlan, selectedIds);

  if (!event && events.length === 0) return;

  const payload: RemoveAssignmentFromAppointmentsRequest = {
    appointmentOccurrences:
      events.length > 0
        ? events.map(({ id, start, schedulingLabel }) => ({
            entityId: id,
            originalOccurrenceStart: start,
            schedulingLabel
          }))
        : [
            {
              entityId: event?.id,
              originalOccurrenceStart: event?.start,
              schedulingLabel: event?.schedulingLabel
            }
          ],
    editAppointmentType
  };

  const unassignPayload = appendCorrelationToken?.(payload) ?? payload;

  setShouldShowPopoverDialog({
    id: '',
    isUnassignment: false,
    isRouteUnassignment: false,
    isRouteConnect: false
  });

  setIsLoading(true);
  unassignAppointmentAsync(unassignPayload)
    .then((response) => {
      if (response.success) {
        setPlanResponsePlanningEntityIdentifiers({
          ts: Date.now(),
          planningEntityIdentifiers: (
            response.planningEntityIdentifiers || []
          ).map(normalizePlanningEntityIdentifier)
        });
      }
    })
    .finally(() => {
      onFinally?.();
    })
    // After `.finally`, so callers still see the same teardown in the same
    // order. This only stops a failed request becoming an unhandled rejection:
    // the caller learns about it through `onFinally`, which runs either way.
    .catch(() => {});
};

/**
 *
 * @param currentPlanningEntityIdentifiers The identifiers as they were sent through the /plan, /unassign-appointment or other request that requires planningEntityIdentifiers and triggers a /planning request
 * @param responsePlanningEntityIdentifiers Identifiers returned by a /plan, /unassign-appointment or websocket response
 * @param selectedPlanningEntityIdentifiers Any filters applied, like a selected employee or client
 * @param planUnitIds An array of plan unit ids, to be converted to PlanningEntityIdentifiers
 * @returns
 */
export const getMatchingPlanningEntityIdentifiers = (
  currentPlanningEntityIdentifiers: PlanningEntityIdentifier[] = [],
  responsePlanningEntityIdentifiers: PlanningEntityIdentifier[] = [],
  selectedPlanningEntityIdentifiers: PlanningEntityIdentifier[] = [],
  planUnitIds: number[] = []
) => {
  const planUnits =
    responsePlanningEntityIdentifiers.length > 0
      ? []
      : planUnitIds.map((id) => ({
          id,
          planningEntityType: 'ORGANIZATIONAL_UNIT' as PlanningEntityType
        }));

  return [
    ...planUnits,
    ...responsePlanningEntityIdentifiers.filter((pei) =>
      currentPlanningEntityIdentifiers.some(
        // TODO: check for ecd standalone
        (cpei) =>
          isSamePlanningEntityIdentifier(
            cpei,
            normalizePlanningEntityIdentifier(pei)
          )
      )
    ),
    ...selectedPlanningEntityIdentifiers
  ];
};

export const getPlanningByResponseEntities = ({
  startDate,
  endDate,
  organizationalUnitPlanningEntityPerspective,
  planningEntityIdentifiers,
  planningEntities,
  threshold = 0
}): Promise<PlanningEntityMap> => {
  const payload: GetPlanningRequest = {
    startDate,
    endDate,
    organizationalUnitPlanningEntityPerspective,
    planningEntityIdentifiers
  };

  return requests.getPlanning(payload).then((res) => {
    const normalizedPlanningEntities = normalizePlanningData(res, threshold);

    const replacedPlanningEntities: PlanningEntityMap = {
      ...planningEntities
    };
    Object.keys(normalizedPlanningEntities).forEach((key) => {
      replacedPlanningEntities[key] = normalizedPlanningEntities[key];
    });

    return replacedPlanningEntities;
  });
};

export const refetchPlanResponsePlanningEntityIdentifiers = (
  setPlanResponsePlanningEntityIdentifiers: ({
    planningEntityIdentifiers,
    ts
  }: {
    planningEntityIdentifiers: PlanningEntityIdentifier[];
    ts: number;
  }) => void,
  planResponseTimestamp?: number
) => {
  setPlanResponsePlanningEntityIdentifiers({
    planningEntityIdentifiers: [],
    ts: planResponseTimestamp || Date.now()
  });
};

export const sortByDate = <T>(arr: T[], dateProp: string, desc?: boolean) =>
  arr.sort((a, b) =>
    dayjs(a[dateProp])[desc ? 'isAfter' : 'isBefore'](b[dateProp]) ? -1 : 1
  );

export const t = i18n.getFixedT(getLocale(), 'common');
